<template>
	<div class="section">
		<div class="container">
			<h1>Cape Town Convention Compliance Index</h1>
			<div class="columns is-multiline">
				<div class="column is-12">
					<p>
						The Cape Town Convention Compliance Index (the
						<b>Index</b>
						) is prepared by the Aviation Working Group to:
					</p>
					<ol>
						<li>
							monitor and assess the implementation and application of the Cape Town Convention and
							its Aircraft Protocol (collectively,
							<b>CTC</b>
							) in countries that have ratified CTC (
							<b>contracting states</b>
							);
						</li>
						<li>provide indicative assessments of future compliance with CTC;</li>
						<li>
							incentivize compliance by providing timely, accurate, and transparent information to
							stakeholders; and
						</li>
						<li>
							provide objective legal risk assessment, which can be used in the transactional
							context.
						</li>
					</ol>
					<p>
						Detailed methodology for the Index's formula and analytical process can be found
						(methodology for score, formula can be found
						<a target="_blank" href="/Scorecard for all annotations.pdf">here</a>
						.
					</p>
					<p>
						Note on the interpretation of watchlist notices issued on the Index can be found
						<a target="_blank" href="/Note on interpretation of watchlist notices.pdf">here</a>
						.
					</p>
					<div v-if="ShowMore">
						<p>
							For the purposes of the Index, compliance means that CTC is fully and effectively
							implemented, prevails over conflicting law, and is being interpreted and applied in
							accordance with its terms and intent.
						</p>
						<p>
							With a score calculated based on a formula designed to reflect both existing CTC
							compliance and project anticipated future CTC compliance, contracting states are
							placed in a corresponding compliance category (very high, high, medium, or low).
						</p>
						<p>
							The Index is updated regularly at semi-annual intervals, in each of March and
							September. In between such regular updates, AWG continues to monitor CTC compliance
							activities in contracting states. In the event there are material developments that
							may impact on a contracting state's Index score, AWG places such contracting state on
							a
							<a @click="$router.push('/about/faq#watchlist')">CTC Compliance Watchlist</a>
							while such material developments are ongoing and being assessed, with a view of
							updating, or confirming, the applicable Index score once the impact of such material
							developments are sufficiently determinable and assessed.
						</p>
						<p>
							For more information on the formula and the analytical process, please see the
							detailed
							<a @click="$router.push('/about/methodology')">methodology</a>
							.
						</p>
					</div>
					<a href="#" @click="ToggleShowMore()">Show {{ ShowMore ? "less" : "more" }}</a>
				</div>
				<div class="column is-3">
					<b-input placeholder="Search for country"
									 type="search"
									 icon="search"
									 v-model="search"></b-input>
				</div>
				<div class="column is-12">
					<div class="columns">
						<div class="column">
							<p :class="{ 'active-tab': qualifying }"
								 style="cursor: pointer"
								 @click="qualifying = true">
								View country scores with qualifying declarations made
								{{ loading ? "" : "(" + Qualifying.length + ")" }}
							</p>
						</div>
						<div class="column">
							<p :class="{ 'active-tab': !qualifying }"
								 style="cursor: pointer"
								 @click="qualifying = false">
								View country scores with no qualifying declarations made
								{{ loading ? "" : "(" + NotQualifying.length + ")" }}
							</p>
						</div>
					</div>
					<div v-show="qualifying">
						<b-table :data="Qualifying"
										 mobile-cards
										 :row-class="HighlightBG"
										 default-sort-direction="desc"
										 :loading="loading"
										 default-sort="Score">
							<b-table-column field="Group" label="Country" sortable v-slot="props">
								<img class="flag-small" :src="getFlag(props.row.LogoPath)" />
								<a @click="goToCountryIndex(props.row.GroupId)">{{ props.row.Group }}</a>
							</b-table-column>
							<b-table-column field="Watchlist" width="30" sortable v-slot="props">
								<a id="watchlist-index"
									 style="border: none"
									 href="javascript://"
									 @click="goToCountryIndexNotices(props.row.GroupId)"
									 v-show="props.row.Watchlist && CanSeeWatchList(props.row.GroupId)">
									<i class="fad fa-exclamation-triangle" v-tooltip="Tooltip($watchlist)"></i>
								</a>
							</b-table-column>
							<b-table-column field="Score"
															label="Score"
															sortable
															v-slot="props"
															:custom-sort="CustomSortScore">
								{{ props.row.Score > 0 ? props.row.Score : "" }}
							</b-table-column>
							<b-table-column field="Category" label="Category" sortable v-slot="props">
								<span v-if="!props.row.IsSpecialNotice">
									{{ props.row.Score != 0 ? props.row.Category : "Unscored" }}
								</span>
								<span v-if="props.row.IsSpecialNotice">Special Notice</span>
							</b-table-column>
							<b-table-column field="QualifyingDeclarations"
															label="Certified-for-transaction version"
															sortable
															v-slot="props">
								<span v-if="props.row.Score != 0">
									<a href="javascript://" @click="print(props.row)">
										<strong>
											<span v-show="CFTAccess(props.row.GroupId)">
												<i class="fad fa-download"></i>
												&nbsp;Download
											</span>
											<span v-show="!CFTAccess(props.row.GroupId)">
												<i class="fad fa-shopping-basket"></i>
												&nbsp;Order
											</span>
											CFT Version
										</strong>
									</a>
									&nbsp;
									<div v-show="props.row.printing">
										<i class="far fa-spinner-third fa-spin"></i>
									</div>
								</span>
							</b-table-column>
						</b-table>
					</div>
					<div v-show="!qualifying">
						<p class="is-paddingless is-marginless">
							<b>Note:</b>
							As qualifying declarations were not made, ratification and compliance with CTC may not
							materially reduce transactional risk.
						</p>
						<b-table :data="NotQualifying"
										 mobile-cards
										 :row-class="HighlightBG"
										 default-sort-direction="desc"
										 :loading="loading"
										 default-sort="Score">
							<b-table-column field="Group" label="Country" sortable v-slot="props">
								<img class="flag-small" :src="getFlag(props.row.LogoPath)" />
								<a @click="goToCountryIndex(props.row.GroupId)">{{ props.row.Group }}</a>
							</b-table-column>
							<b-table-column field="Watchlist" width="30" sortable v-slot="props">
								<a id="watchlist-index"
									 style="border: none"
									 href="javascript://"
									 @click="goToCountryIndexNotices(props.row.GroupId)"
									 v-show="props.row.Watchlist && CanSeeWatchList(props.row.GroupId)">
									<i class="fad fa-exclamation-triangle" v-tooltip="Tooltip($watchlist)"></i>
								</a>
							</b-table-column>
							<b-table-column field="Score" label="Score" sortable v-slot="props">
								{{ props.row.Score != 0 ? props.row.Score : "" }}
							</b-table-column>
							<b-table-column field="Category" label="Category" sortable v-slot="props">
								<span v-if="!props.row.IsSpecialNotice">
									{{ props.row.Score != 0 ? props.row.Category : "Unscored" }}
								</span>
								<span v-if="props.row.IsSpecialNotice">Special Notice</span>
							</b-table-column>
							<b-table-column field="QualifyingDeclarations"
															label="Certified-for-transaction version"
															sortable
															v-slot="props">
								<span v-if="props.row.Score != 0">
									<a href="javascript://" @click="print(props.row)">
										<strong>
											<span v-show="CFTAccess(props.row.GroupId)">
												<i class="fad fa-download"></i>
												&nbsp;Download
											</span>
											<span v-show="!CFTAccess(props.row.GroupId)">
												<i class="fad fa-shopping-basket"></i>
												&nbsp;Order
											</span>
											CFT Version
										</strong>
									</a>
									&nbsp;
									<div v-show="props.row.printing">
										<i class="far fa-spinner-third fa-spin"></i>
									</div>
								</span>
							</b-table-column>
						</b-table>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<script>
	import Role from "@/roles";
	export default {
		data() {
			return {
				data: [],
				search: "",
				qualifying: true,
				ShowMore: false,
				tooltip: {
					delay: { show: 50, hide: 100 },
					autoHide: false,
					html: true,
					content: ""
				},
				products: [],
				loading: false
			};
		},
		computed: {
			CFTDownloadId() {
				if (this.products.length > 0) {
					var products = this.products
						.filter((p) => p.Name.includes("CFT Download"))
						.map((p) => p.Id);
					if (products.length == 1) return products[0];
					else return null;
				} else return null;
			},
			Qualifying() {
				return this.filteredData.filter((item) => item.QualifyingDeclarations == true);
			},
			NotQualifying() {
				return this.filteredData.filter((item) => !item.QualifyingDeclarations);
			},
			filteredData() {
				return this.data.filter((option) => {
					if (option.Group.toString().toLowerCase().indexOf(this.search.toLowerCase()) >= 0) {
						return option.Group.toString().toLowerCase().indexOf(this.search.toLowerCase()) >= 0;
					}
				});
			}
		},
		methods: {
			goToCountryIndex(id) {
				if (id == "a70fc607-1f3b-4957-8734-134da9cdfda0") {
					this.$router.push("/index/" + id);
				} else if (
					(this.$store.getters.user &&
						this.$store.getters.user.Groups &&
						this.$store.getters.user.Groups.some((r) => r == id)) ||
					(this.$store.getters.user &&
						this.$store.getters.user.Roles &&
						this.$store.getters.user.Roles.some(
							(r) =>
								r == Role.Admin || r == Role.Enhanced || r == Role.Standard || r == Role.AWGMember
						))
				) {
					this.$router.push("/index/" + id);
				} else {
					this.$router.push("/restricted");
				}
			},
			goToCountryIndexNotices(id) {
				if (id == "a70fc607-1f3b-4957-8734-134da9cdfda0") {
					this.$router.push("/index/" + id + "#notices");
				} else if (
					(this.$store.getters.user &&
						this.$store.getters.user.Groups &&
						this.$store.getters.user.Groups.some((r) => r == id)) ||
					(this.$store.getters.user &&
						this.$store.getters.user.Roles &&
						this.$store.getters.user.Roles.some(
							(r) =>
								r == Role.Admin || r == Role.Enhanced || r == Role.Standard || r == Role.AWGMember
						))
				) {
					this.$router.push("/index/" + id + "#notices");
				} else {
					this.$router.push("/restricted");
				}
			},
			getProducts() {
				this.$http.get("api/product/quicklist").then((resp) => {
					this.products = resp.data;
				});
			},
			CFTAccess() {
				if (this.$store.getters.user) {
					return this.$store.getters.user.Roles.some(
						(r) =>
							r == Role.Admin || r == Role.Enhanced || r == Role.AWGMember || r == Role.CFTPackage
					);
				}
				return false;
			},
			ToggleShowMore() {
				this.ShowMore = !this.ShowMore;
			},
			getFlag(path) {
				if (!path) return;
				return require("@/assets/images/countryLogos/" + path);
			},
			Refresh() {
				this.loading = true;
				return this.$http
					.get("api/group/index/list")
					.then((resp) => {
						//console.log(resp.data);
						this.data = resp.data;
						this.loading = false;
						return resp.data;
					})
					.catch((err) => {
						this.loading = false;
					});
			},
			HighlightBG(row, index) {
				switch (row.Category) {
					case "Very High": {
						return "is-vhigh-category";
					}
					case "High": {
						return "is-high-category";
					}
					case "Medium": {
						return "is-medium-category";
					}
					case "Low": {
						if (row.Score > 0) {
							return "is-low-category";
						} else {
							return "is-no-score";
						}
					}
				}
			},
			Tooltip(content) {
				return {
					autoHide: false,
					html: true,
					content: content
				};
			},
			CanSeeWatchList(id) {
				var user = this.$store.getters.user;
				if (user) {
					if (
						user.Roles.some(
							(r) =>
								r == Role.Admin ||
								r == Role.Enhanced ||
								r == Role.Standard ||
								r == Role.AWGMember ||
								r == Role.CountryViewer
						)
					) {
						return true;
					} else if (
						user.Roles.some((r) => r == "CountryRespondent" && user.Groups.some((g) => g == id))
					) {
						return true;
					} else {
						return false;
					}
				} else return false;
			},
			print(group) {
				if (this.CFTAccess(group.GroupId)) {
					this.loading = true;
					this.$http
						.get("api/group/index/" + group.GroupId)
						.then((r) => {
							var country = r.data;
							this.$http({
								method: "post",
								url: "api/print/scorecard",
								data: { questionnaire: country },
								responseType: "arraybuffer"
							}).then((resp) => {
								let blob = new Blob([resp.data]);
								var filename = "".concat(country.Name, " Scorecard ", country.Period, ".pdf");

								if (window.navigator && window.navigator.msSaveOrOpenBlob) {
									window.navigator.msSaveOrOpenBlob(blob, filename);
								} else {
									var link = document.createElement("a");
									link.href = window.URL.createObjectURL(blob);
									link.setAttribute("download", filename);
									document.body.appendChild(link);
									link.click();
									document.body.removeChild(link);
								}
								this.loading = false;
							});
						})
						.catch((err) => {
							this.loading = false;
							this.$buefy.toast.open("An error occurred while getting the group's information.");
						});
				} else {
					this.$router.push({
						name: "checkout",
						params: { productId: this.CFTDownloadId, updatesRole: true, groupId: group.GroupId }
					});
				}
			},
			CustomSortScore(a, b, isAsc) {
				const keyA = a.Score + (a.IsSpecialNotice ? 1 : 0);
				const keyB = b.Score + (b.IsSpecialNotice ? 1 : 0);

				if (!isAsc) {
					return keyA < keyB ? 1 : -1;
				} else {
					return keyB < keyA ? 1 : -1;
				}
			}
		},
		created() {
			this.Refresh();
			this.getProducts();
		}
	};
</script>
<style>
	.is-no-score {
		background-color: white;
	}

	.is-low-category {
		background-color: rgba(250, 205, 204, 0.8);
	}

	.is-medium-category {
		background-color: rgba(248, 230, 213, 0.8);
	}

	.is-high-category {
		background-color: rgba(253, 243, 202, 0.8);
	}

	.is-vhigh-category {
		background-color: rgba(226, 241, 216, 0.8);
	}

	nav.tabs ul li.is-active a {
		font-weight: bold !important;
	}
</style>
